<template>
  <div class="ob-col">
    <div class="ob-head">
      <span class="ob-head__icon" style="background: rgba(232, 69, 69, 0.1); color: #e84545"><span class="mdi mdi-calendar-clock"></span></span>
      <h2 class="ob-head__title">Define cuándo vencen los pagos</h2>
      <p class="ob-head__sub">Estos son los valores recomendados. Puedes cambiarlos en cualquier momento desde Configuración</p>
    </div>

    <BillingStartPicker
      v-model:mode="ob.billing.startMode"
      v-model:from="ob.billing.historyFrom"
      v-model:assumed-paid="ob.billing.historyAssumedPaid"
      :default-from="defaultFrom"
    />

    <div class="cards">
      <section v-for="c in cards" :key="c.key" class="bcard">
        <header class="bcard__head">
          <span class="bcard__icon mdi" :class="c.icon"></span>
          <div>
            <h3 class="bcard__title">{{ c.title }}</h3>
            <p class="bcard__desc">{{ c.desc }}</p>
          </div>
        </header>
        <div class="bcard__input">
          <input
            v-model.number="ob.billing[c.key]"
            type="number"
            class="ob-input ob-input--mono bcard__num"
            :min="c.min"
            :max="c.max"
            step="1"
            :aria-label="c.title"
          />
          <span class="bcard__unit">{{ c.unit }}</span>
        </div>
        <p class="bcard__example">{{ c.example }}</p>
      </section>
    </div>

    <div class="ob-hint-box">
      Si no estás seguro de estos valores simplemente continúa. Están configurados con los estándares más comunes del mercado y puedes ajustarlos después.
    </div>

    <p v-if="ob.error" class="ob-error">{{ ob.error }}</p>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { ob } from '@/services/onboardingStore'
import BillingStartPicker from './BillingStartPicker.vue'

const MONTHS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']
const dayMonth = (d) => `${d.getDate()} de ${MONTHS[d.getMonth()]}`
const inDays = (n) => {
  const d = new Date()
  d.setDate(d.getDate() + (Number(n) || 0))
  return dayMonth(d)
}
// Mes sugerido para el historial: el de inicio del contrato si ya se capturó y es pasado.
const defaultFrom = computed(() => {
  const start = ob.contract.startDate
  const now = new Date()
  const current = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  return start && start.slice(0, 7) < current ? start.slice(0, 7) : current
})
const num = (v, fallback) => (Number.isFinite(Number(v)) && v !== '' && v !== null ? Number(v) : fallback)

const cards = computed(() => {
  const b = ob.billing
  const now = new Date()
  const rentDay = Math.min(28, Math.max(1, num(b.rentDueDay, 5)))
  return [
    {
      key: 'rentDueDay', title: 'Renta', desc: 'Día del mes en que vence la renta', unit: 'día del mes', min: 1, max: 28,
      example: `Ejemplo: la renta de ${MONTHS[now.getMonth()]} vence el ${rentDay} de ${MONTHS[now.getMonth()]}.`,
    },
    {
      key: 'serviceDueDays', title: 'Servicios comunes', desc: 'Días de plazo para pagar servicios', unit: 'días', min: 0, max: 120,
      example: `Ejemplo: si cierras servicios hoy ${now.getDate()} de ${MONTHS[now.getMonth()]}, el cargo vence el ${dayMonth(new Date(now.getFullYear(), now.getMonth(), now.getDate() + num(b.serviceDueDays, 15)))}.`,
    },
    {
      key: 'maintenanceDueDays', title: 'Mantenimiento', desc: 'Días de plazo para cargos de mantenimiento', unit: 'días', min: 0, max: 120,
      example: `Ejemplo: un cargo de mantenimiento generado hoy vence el ${inDays(b.maintenanceDueDays)}.`,
    },
    {
      key: 'manualChargeDueDaysDefault', title: 'Cargos manuales', desc: 'Días sugeridos al crear un cargo a mano', unit: 'días', min: 0, max: 120,
      example: `Ejemplo: un cargo creado hoy se sugiere con vencimiento el ${inDays(b.manualChargeDueDaysDefault)}.`,
    },
  ]
})
</script>

<style scoped>
.cards { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.bcard { border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px; background: #fff; display: flex; flex-direction: column; gap: 10px; }
.bcard__head { display: flex; gap: 10px; align-items: flex-start; }
.bcard__icon { font-size: 20px; color: #3b82f6; margin-top: 1px; }
.bcard__title { margin: 0; font: 600 13px Inter, sans-serif; color: #1e3a5f; }
.bcard__desc { margin: 2px 0 0; font: 400 11px Inter, sans-serif; color: #94a3b8; }
.bcard__input { display: flex; align-items: center; gap: 8px; }
.bcard__num { width: 84px; font-size: 16px; text-align: center; }
.bcard__unit { font: 400 12px Inter, sans-serif; color: #64748b; }
.bcard__example { margin: 0; font: 400 11px/1.5 Inter, sans-serif; color: #3b82f6; }
@media (max-width: 640px) { .cards { grid-template-columns: 1fr; } }
</style>